<template>
  <section class="home refreshed-home">
    <div class="hero-copy">
      <p class="eyebrow">Living Photobook</p>
      <h1>This is me…</h1>
      <p class="original-tagline">
        Your family’s most cherished moments, animated and alive.
      </p>
      <p class="lede">
        Sit down with your mom or dad and a favourite photograph. Record them
        telling you about it, then see the person in the photo tell that story
        in their own voice.
      </p>
      <div class="actions">
        <button class="primary" @click="begin">Bring a photo to life ↗</button>
      </div>
      <p class="quiet">
        Create a living portrait together, for the whole family to keep.
      </p>
    </div>
    <figure class="hero-visual original-hero">
      <img
        src="/media/original-car.jpg"
        alt="Vintage family photograph of a woman at the wheel of a convertible"
      />
      <figcaption
        class="photo-prompts"
        aria-label="Stories a photograph can start"
      >
        <span>…when I got my first driver’s license</span>
        <span>…carefree and happy</span>
        <span>…before I met your Grandfather</span>
      </figcaption>
    </figure>
    <div class="hero-visual live-demo family-demo">
      <h2>See a living portrait</h2>
      <p>
        An old photograph of Mary and her son Eric, brought to life with her
        recorded memories.
      </p>
      <div class="demo-tabs" aria-label="Example format">
        <button :aria-pressed="!exampleVideo" @click="showOriginal">
          01 · Original photo</button
        ><button :aria-pressed="exampleVideo" @click="playExample">
          02 · Hear it come to life
        </button>
      </div>
      <div class="photo-frame">
        <div class="example-media">
          <video
            v-if="exampleVideo"
            ref="heroVideo"
            src="/media/washington.mp4"
            poster="/media/washington.jpg"
            controls
            playsinline
            preload="metadata"
            aria-label="Mary telling her story in the animated photograph"
          /><img
            v-else
            src="/media/washington.jpg"
            alt="Original full photograph of Mary with her son Eric in Washington, D.C."
          />
        </div>
        <div class="photo-caption">
          <span>Mary, in her own voice</span
          ><span>Mary and Eric · Washington, D.C.</span>
        </div>
      </div>
      <div class="voice-note">
        <span class="wave" aria-hidden="true">〰</span>
        <div>
          <span class="eyebrow">A real family example</span>
          <p>
            {{
              exampleVideo
                ? "Her recording brings the photo to life."
                : "Press play. Hear the person behind the photo."
            }}
          </p>
        </div>
        <button
          aria-label="Play Mary's living photo with sound"
          @click="playExample"
        >
          ▶
        </button>
      </div>
      <p class="provenance">
        Original photograph &amp; recorded voice · AI-generated movement
      </p>
    </div>
    <section class="how-it-works" aria-labelledby="how-title">
      <div class="section-heading">
        <p class="eyebrow">How it works</p>
        <h2 id="how-title">Make a living portrait together.</h2>
      </div>
      <div class="how-grid">
        <article>
          <span class="step-number">01</span>
          <h3>Choose a photo together</h3>
          <p>
            Look through the album with your parent and pick a photo they want
            to talk about. Select their face in the picture.
          </p>
        </article>
        <article>
          <span class="step-number">02</span>
          <h3>Record their memories</h3>
          <p>
            Ask what was happening when the photo was taken. Who was there?
            Where were they going? Record their answer, or use a recording you
            already have.
          </p>
        </article>
        <article>
          <span class="step-number">03</span>
          <h3>Share it with your family</h3>
          <p>
            Watch the living portrait together. When you’re happy with it, share
            it with children, grandchildren and the rest of the family.
          </p>
        </article>
      </div>
    </section>
    <section class="services" aria-labelledby="services-title">
      <div class="section-heading">
        <p class="eyebrow">Our services</p>
        <h2 id="services-title">
          Start with a photograph. Build a family album.
        </h2>
        <p>
          Try a living portrait together, then choose the memories you’d like to
          keep as a collection.
        </p>
      </div>
      <div class="services-grid">
        <article>
          <span class="service-label">Try it free</span>
          <h3>Your first living portrait</h3>
          <p>
            Choose a photograph and record your parent telling its story. See
            how it feels to watch that moment come to life before making a
            larger album.
          </p>
          <button class="primary" @click="begin">
            Create your first portrait
          </button>
        </article>
        <article>
          <span class="service-label">Build a collection</span>
          <h3>A living photobook</h3>
          <p>
            Bring together their childhood, the people they loved and the family
            you grew up in. Keep the portraits in one place, ready to watch and
            share.
          </p>
          <p class="service-note">Paid album packages are being planned.</p>
        </article>
        <article>
          <span class="service-label">Planned option</span>
          <h3>A film of their memories</h3>
          <p>
            Combine the living portraits into one film, with gentle transitions
            and background music that leaves their voice clear. Something to
            watch together on birthdays, holidays or a quiet afternoon.
          </p>
          <p class="service-note">Montage creation is not available yet.</p>
        </article>
      </div>
      <div class="frame-idea">
        <div>
          <p class="eyebrow">An idea we’re exploring</p>
          <h3>A gift, ready to play.</h3>
        </div>
        <p>
          A video photo frame with the finished family film already loaded. Give
          it to your parent or another family member so they can enjoy the
          memories at home.<br /><span class="service-note"
            >We’re exploring frame options. This is not available to order
            yet.</span
          >
        </p>
      </div>
    </section>
    <section class="story-invitation">
      <div>
        <p class="eyebrow">A question to get you started</p>
        <h2>“What were you like<br />before you had us?”</h2>
      </div>
      <div>
        <p>
          A first car. A holiday with friends. The day they met. Let your parent
          choose a photograph and see where the conversation goes.
        </p>
        <p>You don’t need a script. Just give them time to tell you.</p>
        <button class="primary" @click="begin">Choose a photo →</button>
      </div>
    </section>
    <section class="home-questions" aria-label="About living photographs">
      <h2>A few things to know.</h2>
      <details>
        <summary>Does the recording need to be as old as the photo?</summary>
        <p>
          No. You can record the person today talking about an old photograph.
          Their actual recording becomes the voice of the person in the picture.
        </p>
      </details>
      <details>
        <summary>Can I use a recording I already have?</summary>
        <p>
          Yes. Upload a recording of the selected person telling their story, up
          to two minutes. We use that recording rather than a generated voice.
        </p>
      </details>
      <details>
        <summary>What happens if the animation doesn’t look right?</summary>
        <p>
          Watch the result before sharing it with your family. If something
          looks wrong, keep it private while we help you find a better result.
        </p>
      </details>
    </section>
  </section>
</template>
<script setup>
import { ref, nextTick } from "vue";
import { useRouter } from "vue-router";
const router = useRouter();
const exampleVideo = ref(false);
const heroVideo = ref(null);
const begin = () => router.push("/create");
const showOriginal = () => {
  heroVideo.value?.pause();
  exampleVideo.value = false;
};
const playExample = async () => {
  exampleVideo.value = true;
  await nextTick();
  try {
    await heroVideo.value?.play();
  } catch {
    /* Native controls remain available. */
  }
};
</script>
<style scoped>
.primary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 24px;
  background: var(--accent);
  color: #fff;
  padding: 16px 25px;
  border-radius: 6px;
  font-weight: 600;
  font-size: 15px;
  line-height: 1.5;
}
.primary:hover {
  filter: brightness(0.92);
}
.primary.small {
  padding: 12px 20px;
}
.text-button {
  text-decoration: underline;
  text-underline-offset: 6px;
  font-size: 14px;
  padding: 8px 0;
}
.block {
  display: block;
  margin: 15px auto;
}
.home {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 50px;
  padding-top: 76px;
}
.eyebrow {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 2.5px;
  font-weight: 700;
  color: var(--muted);
  margin: 0 0 18px;
}
h1,
h2,
p {
  margin-top: 0;
}
h1 {
  font: clamp(40px, 5.1vw, 78px) / 1.06 var(--serif);
  letter-spacing: -2px;
  font-weight: 400;
  margin-bottom: 30px;
}
h2 {
  font: 30px/1.2 var(--serif);
  font-weight: 400;
  letter-spacing: -0.5px;
}
.hero-copy {
  padding-top: 25px;
}
.hero-copy h1 {
  max-width: 510px;
}
.lede {
  color: var(--muted);
  font-size: 18px;
  line-height: 1.85;
}
.actions {
  display: flex;
  gap: 24px;
  align-items: center;
  flex-wrap: wrap;
  margin: 32px 0 24px;
}
.quiet {
  font-size: 12px;
  color: var(--muted);
}
.hero-visual {
  position: relative;
  padding: 0 22px 36px 20px;
}
.photo-frame {
  padding: 15px;
  background: var(--paper);
  box-shadow: 0 18px 50px #3c281915;
  transform: rotate(2deg);
}
.photo-frame img {
  width: 100%;
  aspect-ratio: 4/3;
  object-fit: contain;
  background: #e7e3da;
}
.photo-caption {
  display: flex;
  justify-content: space-between;
  padding: 16px 5px 4px;
  font-size: 11px;
  color: var(--muted);
}
.photo-caption span:first-child {
  font: 19px var(--serif);
  color: var(--ink);
}
.voice-note {
  position: relative;
  margin: -4px -20px 0 55px;
  padding: 16px 22px;
  border: 1px solid var(--line);
  border-radius: 8px;
  display: flex;
  gap: 18px;
  align-items: center;
  background: var(--paper);
  box-shadow: 0 8px 28px #3c281910;
}
.voice-note .eyebrow {
  margin: 0;
  font-size: 9px;
}
.voice-note p {
  font: italic 22px var(--serif);
  margin: 3px 0;
}
.voice-note button {
  margin-left: auto;
  border: 1px solid var(--line);
  border-radius: 50%;
  width: 40px;
  height: 40px;
}
.wave {
  font-size: 44px;
  color: var(--accent);
}
.vertical-note {
  position: absolute;
  right: -10px;
  top: 50px;
  writing-mode: vertical-rl;
  font-size: 9px;
  letter-spacing: 3px;
  color: var(--muted);
}
.home-bottom {
  grid-column: 1/-1;
  display: grid;
  grid-template-columns: 1fr 1.3fr 1fr;
  border-top: 1px solid var(--line);
  padding: 38px 0 60px;
  gap: 30px;
}
.chapter {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  color: var(--muted);
}
.home-bottom > p {
  font: 27px/1.4 var(--serif);
}
.steps {
  display: flex;
  flex-direction: column;
  gap: 12px;
  font-size: 13px;
  color: var(--muted);
}

.refreshed-home {
  padding-top: 46px;
  column-gap: 54px;
  row-gap: 64px;
  align-items: center;
}
.refreshed-home .hero-copy {
  padding-top: 0;
}
.refreshed-home .hero-copy h1 {
  font-size: clamp(38px, 4.1vw, 62px);
  max-width: 620px;
  line-height: 1.09;
}
.refreshed-home .lede {
  font-size: 17px;
  line-height: 1.75;
}
.refreshed-home .hero-visual {
  padding: 0;
}
.demo-tabs {
  display: flex;
  gap: 6px;
  margin-bottom: 14px;
}
.demo-tabs button {
  padding: 10px 12px;
  border-bottom: 2px solid transparent;
  font-size: 12px;
  color: var(--muted);
}
.demo-tabs button[aria-pressed="true"] {
  color: var(--ink);
  border-color: var(--accent);
}
.refreshed-home .photo-frame {
  transform: none;
}
.example-media {
  aspect-ratio: 4/3;
  background: var(--soft);
  display: grid;
  place-items: center;
}
.example-media img,
.example-media video {
  display: block;
  width: 100%;
  height: 100%;
  max-height: 420px;
  object-fit: contain;
}
.refreshed-home .voice-note {
  margin: 14px 0 0;
  padding: 13px 15px;
  box-shadow: none;
}
.refreshed-home .voice-note p {
  font-size: 16px;
  line-height: 1.4;
}
.refreshed-home .voice-note button {
  flex-shrink: 0;
}
.provenance {
  font-size: 11px;
  color: var(--muted);
  margin: 14px 0 0;
  text-align: center;
}
.refreshed-home .home-bottom {
  margin: 0;
  padding: 34px 0;
  gap: 24px;
  align-items: start;
}
.refreshed-home .home-bottom > p {
  font-size: 25px;
  margin: 0;
}
.refreshed-home .home-bottom .bridge-copy {
  font: 15px/1.8 inherit;
  color: var(--muted);
  font-family: inherit;
  font-size: 15px;
  line-height: 1.8;
}
.chapter {
  line-height: 1.9;
}
.how-it-works,
.story-invitation,
.home-questions {
  grid-column: 1/-1;
  width: 100%;
  padding-block: 35px;
}
.section-heading h2 {
  font-size: 40px;
  max-width: 650px;
}
.how-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 40px;
  margin-top: 36px;
}
.how-grid article {
  border-top: 1px solid var(--line);
  padding-top: 24px;
}
.step-number {
  font: italic 36px var(--serif);
  color: var(--accent);
}
.how-grid h3 {
  font-size: 19px;
  margin: 18px 0 12px;
}
.how-grid p {
  font-size: 15px;
  line-height: 1.8;
  color: var(--muted);
}
.story-invitation {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 70px;
  background: var(--soft);
  padding: 44px;
  border-radius: 8px;
}
.story-invitation h2 {
  font-size: 44px;
  margin-bottom: 0;
}
.story-invitation p:not(.eyebrow) {
  font-size: 17px;
  line-height: 1.8;
  color: var(--muted);
}
.home-questions {
  max-width: 800px;
  margin: auto;
}
.home-questions h2 {
  font-size: 38px;
}
.home-questions details {
  border-top: 1px solid var(--line);
  padding: 20px 0;
}
.home-questions summary {
  cursor: pointer;
  font-size: 17px;
}
.home-questions details p {
  margin: 15px 0 0;
  color: var(--muted);
  line-height: 1.8;
}

.original-tagline {
  font-size: 24px;
  line-height: 1.4;
}
.original-hero {
  margin: 0;
}
.original-hero img {
  width: 100%;
  display: block;
  height: auto;
}
.original-hero figcaption {
  padding: 18px 0;
  font-family: Georgia, serif;
  font-size: 22px;
}
.refreshed-home .family-demo {
  grid-column: 1 / -1;
  grid-row: auto;
  width: 100%;
  max-width: 720px;
  margin: 24px auto;
}
.family-demo > h2 {
  font-size: 36px;
  margin-bottom: 8px;
}
.family-demo > p {
  margin-bottom: 24px;
}
@media (max-width: 700px) {
  .original-tagline {
    font-size: 22px;
  }
}

.original-hero {
  position: relative;
  container-type: inline-size;
}
.original-hero .photo-prompts {
  position: absolute;
  inset: 0;
  padding: 0;
  font-family: inherit;
  font-size: clamp(11px, 2.5cqw, 20px);
  line-height: 1.35;
  pointer-events: none;
}
.photo-prompts span {
  position: absolute;
  display: block;
  border-radius: 999px;
  background: rgba(248, 246, 240, 0.92);
  color: #292723;
  padding: 0.45em 1em;
  max-width: 92%;
}
.photo-prompts span:nth-child(1) {
  top: 4%;
  left: 5%;
}
.photo-prompts span:nth-child(2) {
  top: 17%;
  right: 4%;
}
.photo-prompts span:nth-child(3) {
  top: 30%;
  left: 10%;
}

.services {
  grid-column: 1 / -1;
  padding: 30px 0;
}
.services .section-heading > p:last-child {
  max-width: 640px;
  line-height: 1.8;
  color: var(--muted);
}
.services-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 24px;
  margin-top: 32px;
}
.services-grid article {
  padding: 28px;
  border: 1px solid var(--line);
  background: #fffdf7;
}
.services h3 {
  font-size: 28px;
  line-height: 1.2;
  margin: 18px 0;
}
.services p {
  line-height: 1.8;
}
.service-label {
  font-size: 13px;
  color: var(--accent);
  font-weight: 600;
}
.service-note {
  font-size: 14px;
  color: var(--muted);
}
.frame-idea {
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 40px;
  border-top: 1px solid var(--line);
  margin-top: 36px;
  padding-top: 24px;
}
@media (max-width: 700px) {
  .services-grid,
  .frame-idea {
    grid-template-columns: 1fr;
    gap: 20px;
  }
  .services-grid article {
    padding: 24px;
  }
  .pv-keepsake .refreshed-home .hero-copy h1 {
    font-size: 64px;
    letter-spacing: 0;
  }
}

.home {
  --muted: var(--ink-2);
  --line: var(--rule);
  --soft: #e8dfcf;
  --serif: "DM Serif Display", Georgia, serif;
}
.refreshed-home .hero-copy h1 {
  font-family: "Allura", cursive;
  font-size: 88px;
  font-weight: 400;
  line-height: 1.1;
  letter-spacing: 0;
}
.original-hero {
  border: 14px solid #fffdf7;
  box-shadow: 0 12px 35px #4530241a;
}
.primary {
  border-radius: 2px;
}
.story-invitation {
  background: #e8dfcf;
  border: 1px solid #d2c4b1;
}
.photo-frame {
  padding: 14px;
  background: #fffdf7;
  box-shadow: 0 12px 35px #45302414;
}
.home h2,
.home h3 {
  font-weight: 400;
}
.home h2 {
  margin-bottom: 18px;
}
.home p {
  margin-bottom: 16px;
}
@media (max-width: 800px) {
  .home {
    grid-template-columns: minmax(0, 1fr);
    gap: 32px;
    padding-top: 12px;
  }
  .refreshed-home .hero-copy h1 {
    font-size: 64px;
  }
  .how-grid,
  .services-grid,
  .frame-idea,
  .story-invitation {
    grid-template-columns: 1fr;
    gap: 24px;
  }
  .story-invitation {
    padding: 26px;
  }
  .section-heading h2,
  .story-invitation h2 {
    font-size: 32px;
  }
  .original-hero {
    border-width: 8px;
  }
  .photo-caption {
    flex-wrap: wrap;
    gap: 8px;
  }
  .voice-note {
    gap: 12px;
  }
  .example-media img,
  .example-media video {
    max-height: none;
    min-height: 0;
  }
}
</style>
